import * as z from "zod";
import { emailAssetId } from "./email-asset";
import { CATALOG_GRID_MAX_PRODUCTS } from "./source/catalog-grid";

// The email of Guestlist as a document of typed blocks (ADR-287 §3, ADR-293), written alike by people, Claude and the
// assistant: sections of columns of blocks, each with a stable id. Colours are tokens of the brand kit, never values;
// the texts are a small inline Markdown (`email-text.ts`); a field of the recipient has a fallback in the document;
// a condition names a closed list of facts. The footer and the unsubscription are not blocks: the code adds them.

/** Stored in every document: a change that is not compatible writes the next one, and reads the old ones. */
export const EMAIL_DOCUMENT_SCHEMA = 1;

/** The languages of a template: one document each (ADR-293). */
export const emailLocales = ["en", "it", "fr", "de", "es"] as const;
export const emailLocale = z.enum(emailLocales);
export type EmailLocale = z.output<typeof emailLocale>;

/** The id of a section, a column, a block or an item: what the edits, the checks and the presence (`row`) name. */
export const elementId = z.string().regex(/^[A-Za-z0-9_-]{1,40}$/, "Letters, digits, '_' and '-', up to 40");

/** The fields of the recipient a text names as `{{contact.firstName}}`, each with its fallback in `merge`. */
export const mergeFields = [
  "contact.firstName",
  "contact.lastName",
  "contact.jobTitle",
  "account.name",
  "account.storeName",
  "campaign.name",
] as const;
export type MergeField = (typeof mergeFields)[number];

/** The facts a block may be shown for (`visibleIf`): `account.isCustomer` is `"true"` or `"false"`. */
export const conditionFields = [
  "account.countryCode",
  "account.stage",
  "account.isCustomer",
  "contact.role",
  "invitation.status",
] as const;
export type ConditionField = (typeof conditionFields)[number];

/**
 * The values a fact can take, where they are a closed list (ADR-295): the stages of the projection (`accountStages` of
 * `crm/source`, which this part of the contract may not import) and the statuses of an invitation of booking (ADR-291,
 * not a contract crm may name); a test keeps the copies equal. A country is two capital letters; the role of a contact
 * is free text of party.
 */
export const conditionValues = {
  "account.stage": ["prospect", "customer"],
  "account.isCustomer": ["true", "false"],
  "invitation.status": ["invited", "booked", "visited", "no_show", "declined", "withdrawn"],
} as const satisfies Partial<Record<ConditionField, readonly string[]>>;
const COUNTRY_CODE = /^[A-Z]{2}$/;

/** Whether a value can be one of the fact: a value no recipient has hides or shows a block for everyone. */
export function isConditionValue(field: ConditionField, value: string): boolean {
  if (field === "account.countryCode") return COUNTRY_CODE.test(value);
  if (field === "contact.role") return true;
  return (conditionValues[field] as readonly string[]).includes(value);
}

/** Shown only to the recipients whose fact is (`in`) or is not (`not_in`) one of the values; a fact a recipient lacks is in no list. */
export const visibleIf = z.object({
  field: z.enum(conditionFields),
  op: z.enum(["in", "not_in"]),
  values: z.array(z.string().trim().min(1).max(40)).min(1).max(50),
});
export type VisibleIf = z.output<typeof visibleIf>;

/** A link: `https:` or `mailto:`, checked by `checkEmailDocument` so that a draft keeps what is being typed. */
const href = z.string().trim().max(2000);
const align = z.enum(["left", "center", "right"]);
const buttonStyle = z.enum(["primary", "accent", "outline"]);
/** Inline Markdown: `**bold**`, `*italic*`, `[label](https://…)`, `{{field}}`; a blank line starts a paragraph. */
const inlineText = (max: number) => z.string().max(max);
/** An image of the asset store of crm, by its SHA-256 (ADR-293); `null` while none is chosen. */
const asset = emailAssetId.nullable();

const base = { id: elementId, visibleIf: visibleIf.optional() };

const heading = z.object({
  ...base,
  type: z.literal("heading"),
  level: z.number().int().min(1).max(3),
  text: inlineText(300),
  align: align.optional(),
});
/** Paragraphs, and lists whose lines start with `- `. */
const text = z.object({ ...base, type: z.literal("text"), text: inlineText(5000), align: align.optional() });
const image = z.object({
  ...base,
  type: z.literal("image"),
  assetId: asset,
  /** What the image says, for whoever does not see it; empty only with `decorative`. */
  alt: z.string().max(300),
  decorative: z.boolean().optional(),
  href: href.optional(),
  /** Of the width of the column, in percent; 100 if absent. */
  width: z.number().int().min(10).max(100).optional(),
});
const button = z.object({
  ...base,
  type: z.literal("button"),
  label: z.string().max(80),
  href,
  style: buttonStyle.optional(),
  align: align.optional(),
});
/** The personal booking link of the recipient for the campaign of the send (ADR-237), written by the code. */
const bookingButton = z.object({
  ...base,
  type: z.literal("booking_button"),
  label: z.string().max(80),
  style: buttonStyle.optional(),
  align: align.optional(),
});
const productItem = z.object({
  id: elementId,
  assetId: asset,
  name: z.string().max(120),
  caption: z.string().max(200).optional(),
  href: href.optional(),
});
/** Products written by hand; from a linesheet or a brand in P8.14r, as a new field. */
const productGrid = z.object({
  ...base,
  type: z.literal("product_grid"),
  columns: z.number().int().min(2).max(3),
  items: z.array(productItem).max(CATALOG_GRID_MAX_PRODUCTS),
});
const logoItem = z.object({ id: elementId, assetId: asset, alt: z.string().max(300), href: href.optional() });
const logoStrip = z.object({ ...base, type: z.literal("logo_strip"), items: z.array(logoItem).max(12) });
const eventCard = z.object({
  ...base,
  type: z.literal("event_card"),
  title: z.string().max(200),
  startDate: z.iso.date().nullable(),
  endDate: z.iso.date().nullable().optional(),
  /** As written, e.g. "10:00–18:00". */
  time: z.string().max(60).optional(),
  place: z.string().max(200),
  address: z.string().max(300).optional(),
  note: inlineText(1000).optional(),
});
const divider = z.object({ ...base, type: z.literal("divider") });
const spacer = z.object({ ...base, type: z.literal("spacer"), size: z.enum(["s", "m", "l"]) });

export const emailBlock = z.discriminatedUnion("type", [
  heading,
  text,
  image,
  button,
  bookingButton,
  productGrid,
  logoStrip,
  eventCard,
  divider,
  spacer,
]);
export type EmailBlock = z.output<typeof emailBlock>;
export type EmailBlockType = EmailBlock["type"];

// What an edit sends: the items of a grid or a strip may come without an id, and the domain gives one.
const productGridInput = productGrid.extend({
  items: z.array(productItem.partial({ id: true })).max(CATALOG_GRID_MAX_PRODUCTS),
});
const logoStripInput = logoStrip.extend({ items: z.array(logoItem.partial({ id: true })).max(12) });
const inputs = [
  heading,
  text,
  image,
  button,
  bookingButton,
  productGridInput,
  logoStripInput,
  eventCard,
  divider,
  spacer,
] as const;

/** A block as an edit writes it whole, with its id. */
export const emailBlockInput = z.discriminatedUnion("type", inputs);
export type EmailBlockInput = z.output<typeof emailBlockInput>;

/** A block to add: without `id` the domain gives one. */
export const newEmailBlock = z.discriminatedUnion("type", [
  heading.partial({ id: true }),
  text.partial({ id: true }),
  image.partial({ id: true }),
  button.partial({ id: true }),
  bookingButton.partial({ id: true }),
  productGridInput.partial({ id: true }),
  logoStripInput.partial({ id: true }),
  eventCard.partial({ id: true }),
  divider.partial({ id: true }),
  spacer.partial({ id: true }),
]);
export type NewEmailBlock = z.output<typeof newEmailBlock>;

/** A column of the grid of 12: the widths of a section add up to 12. */
export const emailColumn = z.object({
  id: elementId,
  width: z.number().int().min(1).max(12),
  blocks: z.array(emailBlock).max(30),
});
export type EmailColumn = z.output<typeof emailColumn>;

/** `page` sets a section apart from the card with the colour around it. */
export const sectionBackgrounds = ["none", "page", "primary", "accent"] as const;
export const emailSection = z.object({
  id: elementId,
  background: z.enum(sectionBackgrounds).optional(),
  columns: z.array(emailColumn).min(1).max(3),
});
export type EmailSection = z.output<typeof emailSection>;

export const emailDocument = z
  .object({
    schema: z.literal(EMAIL_DOCUMENT_SCHEMA),
    /** The brand kit (`brandKitDto`). */
    themeId: z.uuid(),
    locale: emailLocale,
    subject: inlineText(200),
    preheader: inlineText(200),
    /** The fallback of every field the texts name, for a recipient without it. */
    merge: z.partialRecord(z.enum(mergeFields), z.object({ fallback: z.string().max(100) })),
    sections: z.array(emailSection).max(40),
  })
  .meta({ id: "EmailDocument" });
export type EmailDocument = z.output<typeof emailDocument>;
